/** 오늘 — what to look at first (S1).
 *
 *  The tracker's state and its run button, four tiles, the list of things to
 *  check, the portfolio, the best keyword opportunities and today's rank
 *  moves. Replaces the old Dashboard and the top of the old Tracking page; the
 *  app cards went to /apps, the change log to 변동 > 기록 and the competition
 *  board to the keyword drawer.
 *
 *  The "확인할 것" list is the attention strip's items plus what only this page
 *  says (a collector not set up, telemetry with no app), so the page draws no
 *  strip of its own: `attention={false}`. */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Boxes, Play, X } from 'lucide-react'
import { api, type DashboardStats } from '@/api/client'
import { setupNotes, withoutDismissed, type AttentionItem, type AttentionTone } from '@/lib/attention'
import { useOpenKeyword } from '@/lib/detail'
import {
  qk,
  useAnalyticsApps,
  useAnalyticsStatus,
  useAttentionDismissals,
  useDashboard,
  useTrackingStatus,
  useWatchedRuns,
} from '@/lib/queries'
import { trackerView } from '@/lib/tracker'
import { cn } from '@/lib/utils'
import {
  AppIcon,
  Button,
  ButtonLink,
  ConfirmDialog,
  CountryTag,
  DataTable,
  Delta,
  EmptyState,
  errorText,
  IconButton,
  PageHeader,
  ScoreNumber,
  StatTile,
  StatusDot,
  StatusPill,
  useAttentionItems,
  useDismissItem,
  useToast,
  When,
  type Column,
} from '@/ui'

export interface Note {
  key: string
  tone: AttentionTone
  label: string
  count: number
  to: string
  /** Where it happened and what it was, one short line. */
  line: string
  /** When it happened or when it frees up ("다음 허용 시각"), if the item says. */
  at?: string
  /** The service's own words, whole. */
  message: string
  /** Set when the note can be cleared by hand (AttentionItem.stamp). */
  stamp?: string
}

const TONE_ORDER: Record<AttentionTone, number> = { failed: 0, publishing: 1, neutral: 2 }

/** The "확인할 것" list: everything the strip would say, then what only this
 *  page adds. Failures first; within a tone the order the items came in. */
export function todayNotes(strip: AttentionItem[], setup: AttentionItem[]): Note[] {
  return [...strip, ...setup]
    .map((i, n) => ({ i, n }))
    .sort((a, b) => TONE_ORDER[a.i.tone] - TONE_ORDER[b.i.tone] || a.n - b.n)
    .map(({ i }) => {
      const d = i.details[0]
      return {
        key: i.key,
        tone: i.tone,
        label: i.label,
        count: i.count,
        to: i.to,
        line: d ? [d.where, d.what].filter(Boolean).join(' · ') : '',
        at: d?.at,
        message: d?.message ?? '',
        stamp: i.stamp,
      }
    })
}

const WEEKDAY_LONG = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일']

/** "10월 17일 토요일" — the header line. */
const longDay = (d: Date) => `${d.getMonth() + 1}월 ${d.getDate()}일 ${WEEKDAY_LONG[d.getDay()]}`

type Portfolio = DashboardStats['portfolio'][number]
type Opportunity = DashboardStats['opportunities'][number]

export default function Today() {
  const dash = useDashboard()
  const tracking = useTrackingStatus()
  const runs = useWatchedRuns()
  const analytics = useAnalyticsStatus(true)
  const telemetry = useAnalyticsApps(30)
  const strip = useAttentionItems()
  const dismissals = useAttentionDismissals()
  const dismiss = useDismissItem()
  const qc = useQueryClient()
  const toast = useToast()
  const [confirm, setConfirm] = useState(false)

  const d = dash.data
  const status = tracking.data
  const view = trackerView(status, runs.runs)
  const running = view.key === 'running'

  const run = useMutation({
    mutationFn: api.trackingRun,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.trackingStatus })
      toast.success('스냅샷을 시작했다')
    },
    onError: (e) => toast.error(errorText(e)),
  })

  const notes = useMemo(
    () =>
      todayNotes(
        strip,
        withoutDismissed(setupNotes({ analytics: analytics.data, analyticsApps: telemetry.data?.items }), dismissals.data?.items),
      ),
    [strip, analytics.data, telemetry.data, dismissals.data],
  )

  return (
    <>
      <PageHeader
        title="오늘"
        description={longDay(new Date())}
        attention={false}
        actions={
          <Button variant="primary" icon={Play} disabled={running || run.isPending} onClick={() => setConfirm(true)}>
            {running ? '스냅샷 실행 중' : '스냅샷 지금 실행'}
          </Button>
        }
      />

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="스냅샷을 지금 실행할까"
        description="iTunes 등 외부 서비스에 순위를 조회한다. 추적 키워드가 많으면 몇 분 걸린다."
        confirmLabel="실행"
        onConfirm={async () => {
          try {
            await run.mutateAsync()
          } catch {
            /* the toast already says why */
          }
          setConfirm(false)
        }}
      />

      <section aria-label="스냅샷 상태" className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-table">
        <StatusPill domain="tracking" status={view.key} />
        <span className="text-muted-foreground">
          마지막 {view.lastAt ? <When iso={view.lastAt} mode="relative" className="text-foreground" /> : '없음'}
          {view.from === 'jobs' && (
            <span className="ml-1.5" title="서버를 다시 띄운 뒤라 트래커 상태가 비어 있어, 작업 기록의 마지막 스냅샷을 보여 준다">
              (작업 기록 기준)
            </span>
          )}
        </span>
        <span className="text-muted-foreground">
          주기 <span className="tabular-nums text-foreground">{status?.interval ?? '—'}</span>시간
        </span>
        <span className="text-muted-foreground">
          Top-N <span className="tabular-nums text-foreground">{status?.top_n ?? '—'}</span>
        </span>
        {view.key === 'error' && view.message && (
          <span className="min-w-0 max-w-full truncate font-mono text-meta text-status-failed" title={view.message}>
            {view.message}
          </span>
        )}
      </section>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="내 앱" value={d?.apps_count ?? 0} to="/apps" />
        <StatTile label="추적 키워드" value={d?.keywords_count ?? 0} hint={`순위 밖 ${d?.unranked_count ?? 0}`} />
        <StatTile label="Top 10" value={d?.top10_count ?? 0} hint={`11~50위 ${d?.top50_count ?? 0}`} />
        <StatTile
          label="포트폴리오 점수"
          value={d?.avg_overall ? d.avg_overall.toFixed(1) : '—'}
          hint={`분석 ${d?.analysis_count ?? 0} / ${d?.apps_count ?? 0}`}
          to="/compare"
        />
      </div>

      <Notes notes={notes} onDismiss={dismiss} />

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <section aria-label="포트폴리오" className="min-w-0 space-y-2 lg:col-span-3">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-section font-semibold">포트폴리오</h2>
            <Link to="/compare" className="text-meta font-medium text-accent hover:underline">
              비교
            </Link>
          </div>
          <PortfolioTable dash={d} loading={dash.isLoading} error={dash.error} onRetry={() => dash.refetch()} />
        </section>
        <section aria-label="기회" className="min-w-0 space-y-2 lg:col-span-2">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <h2 className="text-section font-semibold">기회</h2>
            <span className="text-meta text-muted-foreground">찾는 사람이 있고 상위가 약한데 우리가 11~100위인 문구</span>
          </div>
          <OpportunityTable dash={d} loading={dash.isLoading} error={dash.error} onRetry={() => dash.refetch()} />
        </section>
      </div>

      <section aria-label="오늘의 변동" className="mt-6 space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-section font-semibold">오늘의 변동</h2>
          <Link to="/movement?period=daily" className="text-meta font-medium text-accent hover:underline">
            전부 보기
          </Link>
        </div>
        <Movers dash={d} loading={dash.isLoading} />
      </section>
    </>
  )
}

// ── 확인할 것 ───────────────────────────────────────────────────────────────

/** Each note opens where it happened; one that can be cleared has an ×
 *  that takes it off the list until the same thing happens again. */
function Notes({ notes, onDismiss }: { notes: Note[]; onDismiss: (n: Note) => void }) {
  return (
    <section aria-label="확인할 것" className="space-y-2">
      <h2 className="flex items-baseline gap-2 text-section font-semibold">
        확인할 것
        <span className="text-table tabular-nums text-muted-foreground">{notes.length}</span>
      </h2>
      {notes.length === 0 ? (
        <p className="rounded-lg border border-border bg-card px-3 py-3 text-table text-muted-foreground">지금 확인할 것이 없다.</p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {notes.map((n) => (
            <li key={n.key} className="flex items-start">
              <Link to={n.to} className="flex min-w-0 flex-1 items-start gap-2.5 px-3 py-2.5 text-table transition-colors hover:bg-muted/60">
                <StatusDot tone={n.tone} className="mt-1.5" />
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">
                    {n.label}
                    {n.count > 0 && <span className="ml-1.5 tabular-nums text-muted-foreground">{n.count}</span>}
                  </span>
                  {(n.line || n.at) && (
                    <span className="block break-words text-meta text-muted-foreground">
                      {n.line}
                      {n.at && (
                        <>
                          {n.line && ' '}
                          <When iso={n.at} mode="slot" className="tabular-nums text-foreground" />
                        </>
                      )}
                    </span>
                  )}
                  {n.message && (
                    <span className="mt-0.5 line-clamp-2 block break-all font-mono text-micro text-muted-foreground">{n.message}</span>
                  )}
                </span>
              </Link>
              {n.stamp !== undefined && (
                <IconButton
                  icon={X}
                  size="sm"
                  variant="quiet"
                  label={`${n.label} 지우기`}
                  title="목록에서 지운다. 같은 일이 다시 생기면 다시 뜬다. 작업 기록은 그대로 남는다."
                  className="mr-1.5 mt-1.5"
                  onClick={() => onDismiss(n)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ── 포트폴리오 ──────────────────────────────────────────────────────────────

interface ListProps {
  dash?: DashboardStats
  loading: boolean
  error?: unknown
  onRetry?: () => void
}

function PortfolioTable({ dash, loading, error, onRetry }: ListProps) {
  const navigate = useNavigate()
  const columns: Column<Portfolio>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (p) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <AppIcon src={p.app.icon_url} name={p.app.name} size={28} />
          <span className="min-w-0 truncate font-medium">{p.app.name}</span>
        </span>
      ),
    },
    {
      key: 'best',
      header: '최고 순위',
      cell: (p) =>
        p.best_term ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <span className="font-semibold tabular-nums">#{p.best_rank}</span>
            <span className="min-w-0 truncate">{p.best_term}</span>
            <CountryTag code={p.best_country} />
          </span>
        ) : (
          <span className="text-muted-foreground">순위 밖</span>
        ),
    },
    { key: 'keywords', header: '키워드', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (p) => p.keyword_count },
    { key: 'top10', header: 'Top 10', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (p) => p.top_10 },
    {
      key: 'counts',
      header: '키워드 · Top 10',
      cardOnly: true,
      cell: (p) => (
        <span className="tabular-nums">
          {p.keyword_count} <span className="text-muted-foreground">·</span> {p.top_10}
        </span>
      ),
    },
    { key: 'score', header: '점수', align: 'right', card: 'meta', cell: (p) => <ScoreNumber value={p.overall_score} /> },
  ]
  return (
    <DataTable
      label="포트폴리오"
      columns={columns}
      rows={dash?.portfolio}
      rowKey={(p) => p.app.id}
      loading={loading}
      error={error}
      onRetry={onRetry}
      onRowClick={(p) => navigate(`/apps/${p.app.id}`)}
      empty={
        <EmptyState
          icon={Boxes}
          title="내 앱이 아직 없다"
          description="App Store Connect에서 앱을 가져오면 여기에 순위와 점수가 쌓인다."
          actions={<ButtonLink to="/apps">앱 열기</ButtonLink>}
        />
      }
    />
  )
}

// ── 기회 ────────────────────────────────────────────────────────────────────

/** Gaps, read from the store: the term is searched (Apple's chart or
 *  autocomplete), none of the top apps has it in the name or their ratings are
 *  few, and we are 11th to 100th in store order. No tier pill: that speaks of
 *  another scale, and the columns here are the reasons a row is listed. */
function OpportunityTable({ dash, loading, error, onRetry }: ListProps) {
  const open = useOpenKeyword()
  const columns: Column<Opportunity>[] = [
    {
      key: 'term',
      header: '키워드',
      card: 'title',
      cell: (o) => (
        <span className="flex min-w-0 items-center gap-2">
          <AppIcon src={o.app.icon_url} name={o.app.name} size={24} />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5">
              <span className="truncate font-medium">{o.term}</span>
              <CountryTag code={o.country} />
            </span>
            <span className="block truncate text-meta text-muted-foreground">{o.app.name}</span>
          </span>
        </span>
      ),
    },
    {
      key: 'rank',
      header: '순위',
      align: 'right',
      className: 'whitespace-nowrap tabular-nums',
      card: 'meta',
      cell: (o) => (o.rank > 0 ? `#${o.rank}` : <span className="text-muted-foreground">순위 밖</span>),
    },
    { key: 'pop', header: '인기도', align: 'right', className: 'tabular-nums', card: 'line', cell: (o) => o.popularity },
    {
      key: 'holders',
      header: <span title="스토어 상위 앱 중 이름에 이 문구가 있는 앱 수">점유</span>,
      align: 'right',
      className: 'whitespace-nowrap tabular-nums',
      card: 'line',
      cell: (o) => `${o.name_holders}/${o.top_count}`,
    },
    {
      key: 'ratings',
      header: <span title="스토어 상위 앱의 평점 수 중앙값">평점</span>,
      align: 'right',
      className: 'whitespace-nowrap tabular-nums',
      card: 'line',
      cell: (o) => o.median_ratings.toLocaleString('ko-KR'),
    },
  ]
  return (
    <DataTable
      label="기회"
      columns={columns}
      rows={dash?.opportunities.slice(0, 8)}
      rowKey={(o) => o.keyword_id}
      loading={loading}
      error={error}
      onRetry={onRetry}
      onRowClick={(o) => open(o.app.id, o.keyword_id)}
      empty={<EmptyState title="기회가 아직 없다" description="우선 문구의 스토어 순위와 수요를 읽으면 여기에 올라온다." />}
    />
  )
}

// ── 오늘의 변동 ─────────────────────────────────────────────────────────────

function Movers({ dash, loading }: { dash?: DashboardStats; loading: boolean }) {
  const columns: Column<DashboardStats['movers'][number]>[] = [
    {
      key: 'delta',
      header: '변동',
      card: 'meta',
      width: '170px',
      cell: (m) => <Delta status={m.kind} delta={m.delta} before={m.rank_before} after={m.rank_after} />,
    },
    {
      key: 'term',
      header: '키워드',
      card: 'title',
      cell: (m) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium">{m.term}</span>
          <CountryTag code={m.country} />
        </span>
      ),
    },
    {
      key: 'app',
      header: '앱',
      card: 'hidden',
      cell: (m) => (
        <span className="inline-flex min-w-0 items-center gap-2">
          <AppIcon src={m.app.icon_url} name={m.app.name} size={20} />
          <span className="truncate">{m.app.name}</span>
        </span>
      ),
    },
    { key: 'at', header: '시각', align: 'right', className: cn('whitespace-nowrap text-muted-foreground'), card: 'hidden', cell: (m) => <When iso={m.at} /> },
    {
      // the phone's one line under the keyword
      key: 'appAt',
      header: '앱 · 시각',
      card: 'sub',
      cardOnly: true,
      cell: (m) => (
        <span className="flex min-w-0 items-center gap-2">
          <AppIcon src={m.app.icon_url} name={m.app.name} size={20} />
          <span className="min-w-0 truncate">{m.app.name}</span>
          <span aria-hidden>·</span>
          <When iso={m.at} className="shrink-0 whitespace-nowrap" />
        </span>
      ),
    },
  ]
  return (
    <DataTable
      label="오늘의 변동"
      columns={columns}
      rows={dash?.movers.slice(0, 6)}
      rowKey={(m) => `${m.app.id}:${m.country}:${m.term}:${m.at}`}
      loading={loading}
      empty={<EmptyState title="순위 변동이 아직 없다" description="서로 다른 날 스냅샷이 두 번 쌓이면 변동이 여기에 나온다." />}
    />
  )
}
